/**
 * ThemeProvider.tsx — light/dark theming with a three-way System · Light · Dark switch.
 *
 * Lifted from apps/chatter-app/src/lib/theme.tsx (D-02).
 *
 * Design Spec §08: the app follows the OS by default; the switch in the header
 * sets `data-theme` on <html> to override. The accent token is independent of
 * the neutral scale (see each app's index.css), so "System" mode stays correct.
 *
 * Mechanism:
 *   - mode 'system' → remove the `data-theme` attribute → CSS `prefers-color-scheme` wins.
 *   - mode 'light' | 'dark' → set `data-theme` → manual override wins.
 * Persisted in localStorage under `maloum.theme`.
 */

import * as React from 'react';

export type ThemeMode = 'system' | 'light' | 'dark';

const STORAGE_KEY = 'maloum.theme';

interface ThemeContextValue {
  mode: ThemeMode;
  setMode: (mode: ThemeMode) => void;
}

const ThemeContext = React.createContext<ThemeContextValue | null>(null);
ThemeContext.displayName = 'ThemeContext';

function readStoredMode(): ThemeMode {
  if (typeof window === 'undefined') return 'system';
  const stored = window.localStorage.getItem(STORAGE_KEY);
  return stored === 'light' || stored === 'dark' ? stored : 'system';
}

function applyMode(mode: ThemeMode): void {
  const root = document.documentElement;
  if (mode === 'system') {
    root.removeAttribute('data-theme');
  } else {
    root.setAttribute('data-theme', mode);
  }
}

export function ThemeProvider({ children }: { children: React.ReactNode }) {
  const [mode, setModeState] = React.useState<ThemeMode>(readStoredMode);

  // Apply on mount + whenever the mode changes.
  React.useEffect(() => {
    applyMode(mode);
  }, [mode]);

  const setMode = React.useCallback((next: ThemeMode) => {
    setModeState(next);
    try {
      if (next === 'system') window.localStorage.removeItem(STORAGE_KEY);
      else window.localStorage.setItem(STORAGE_KEY, next);
    } catch {
      /* localStorage unavailable — non-fatal, theme still applies for the session */
    }
  }, []);

  const value = React.useMemo(() => ({ mode, setMode }), [mode, setMode]);

  return <ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>;
}

export function useTheme(): ThemeContextValue {
  const ctx = React.useContext(ThemeContext);
  if (!ctx) throw new Error('useTheme called outside <ThemeProvider>');
  return ctx;
}
